import React from "react";
import { ProductDetailsModal } from "../../../../../../interfaces/ProductObject";
import { DINE_IN_ITEM_STATUS, resolveProductLine } from "../../../../../../services/helpers/DineInSummaryHelper";
import { DineInKitchenItem } from "./dineInDetailBuilder";
import { itemStatusMeta } from "./dineInBoardStatusDefs";
import { DineInBoardActions } from "./useDineInBoardActions";
import "./styles/DineInItemStates.css";
import "./styles/DineInDetailItemRow.css";

interface DineInDetailItemRowProps {
  item: DineInKitchenItem;
  /** Undefined while the product is still being resolved. */
  product?: ProductDetailsModal;
  actions: DineInBoardActions;
}

/**
 * One ticket line.
 *
 * Which buttons appear is derived entirely from the item's status — there is
 * no separate "mode" to keep in sync:
 *   ORDERED   -> Accept / Reject   (the kitchen hasn't decided)
 *   ACCEPTED  -> Served            (decided, not yet on the table)
 *   REJECTED  -> nothing           (terminal)
 *   SERVED    -> nothing           (terminal)
 * An unrecognised status falls through to no buttons, which is the safe
 * default: better an action staff can't take than one that means something
 * different from what this build assumes.
 */
const DineInDetailItemRow: React.FC<DineInDetailItemRowProps> = ({
  item,
  product,
  actions,
}) => {
  // The kitchen has to cook the right portion, so the variant is part of the name.
  const title = resolveProductLine(product, item.variantId).displayTitle || "Menu item";
  const meta = itemStatusMeta(item.status);

  // Gated on status ALONE. An earlier version also required a server id, which
  // meant a missing id hid the buttons entirely — indistinguishable from "this
  // item needs nothing", and therefore invisible. The handler already refuses
  // and explains when there's no id to act on, so an honest error beats a
  // button that quietly isn't there.
  const isPending = item.status === DINE_IN_ITEM_STATUS.ORDERED;
  const isAccepted = item.status === DINE_IN_ITEM_STATUS.ACCEPTED;
  const isTerminal =
    item.status === DINE_IN_ITEM_STATUS.SERVED ||
    item.status === DINE_IN_ITEM_STATUS.REJECTED;

  return (
    <div
      className={`dine-in-row dine-in-item--${meta.modifier} ${
        isTerminal ? "dine-in-row--terminal" : ""
      }`}
    >
      <div className="dine-in-row__main">
        <div className="dine-in-row__text">
          <span className="dine-in-row__title">{title}</span>
          <span className="dine-in-row__meta">
            ×{item.quantity}
            {item.lineTotal != null && ` · ₹${Math.round(item.lineTotal)}`}
          </span>
        </div>
        <span className="dine-in-row__status">{meta.label.toUpperCase()}</span>
      </div>

      {isPending && (
        <div className="dine-in-row__actions">
          <button
            type="button"
            className="dine-in-row__btn dine-in-row__btn--accept"
            onClick={() => actions.acceptItem(item)}
            disabled={actions.isDeciding}
            aria-busy={actions.isBusy(item.key, "accept")}
          >
            {actions.isBusy(item.key, "accept") ? <span className="dine-in-btn-spinner" /> : "Accept"}
          </button>
          <button
            type="button"
            className="dine-in-row__btn dine-in-row__btn--reject"
            onClick={() => actions.requestReject(item)}
            disabled={actions.isDeciding}
            aria-busy={actions.isBusy(item.key, "reject")}
          >
            {actions.isBusy(item.key, "reject") ? <span className="dine-in-btn-spinner" /> : "Reject"}
          </button>
        </div>
      )}

      {isAccepted && (
        <div className="dine-in-row__actions">
          <button
            type="button"
            className="dine-in-row__btn dine-in-row__btn--deliver"
            onClick={() => actions.deliverItem(item)}
            disabled={actions.isDeciding}
            aria-busy={actions.isBusy(item.key, "serve")}
          >
            {actions.isBusy(item.key, "serve") ? <span className="dine-in-btn-spinner" /> : "Served"}
          </button>
        </div>
      )}
    </div>
  );
};

export default DineInDetailItemRow;
